import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { FileText, Database, Calendar, Eye, Search, Filter, Info, Link, CalendarDays } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/shared/components/ui/input'
import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { useConfiguraciones } from '@/modules/registros/hooks/useConfiguraciones'
import { useAuth } from '@/auth/context/AuthContext'
import { useTeam } from '@/shared/context/TeamContext'

export function ReportesPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [search, setSearch] = useState('')
  const [showInactive, setShowInactive] = useState(false)
  const [showInfo, setShowInfo] = useState(false)

  // Check if user is admin
  const isTonalAdmin = useMemo(() => {
    if (!user) return false
    return user.is_superuser || user.groups.includes('admin')
  }, [user])

  // Filtrar por área seleccionada
  const { selectedArea, selectedTeam } = useTeam()
  const isClientArea = selectedTeam !== 'Admin' && selectedTeam !== 'Tonal'
  const areaGrupoId = isClientArea ? selectedArea?.laboratorio_id : undefined

  const { data: rawData, isLoading, error } = useConfiguraciones({
    page: 1,
    page_size: 100,
    search: search || undefined,
    activo: showInactive ? undefined : true,
    grupo_id: areaGrupoId ?? undefined,
  })

  // Group configs with the same dashboard_grupo_id into a single card
  const data = useMemo(() => {
    if (!rawData) return rawData
    const seen = new Set<string>()
    const grouped = rawData.results.filter((config) => {
      if (config.dashboard_grupo_id && config.frontend_personalizado) {
        if (seen.has(config.dashboard_grupo_id)) return false
        seen.add(config.dashboard_grupo_id)
      }
      return true
    })
    return { ...rawData, results: grouped, count: grouped.length }
  }, [rawData])

  const handleViewReport = (config: { id: number; frontend_personalizado?: boolean; dashboard_grupo_id?: string }) => {
    const url = config.frontend_personalizado && config.dashboard_grupo_id
      ? `/registros/dashboard/${config.dashboard_grupo_id}`
      : `/registros/reporte/${config.id}`
    window.open(url, '_blank')
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="max-w-6xl mx-auto">
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6 text-center">
            <FileText className="mx-auto h-12 w-12 text-destructive mb-4" />
            <h3 className="text-lg font-semibold text-destructive mb-2">Error al cargar reportes</h3>
            <p className="text-sm text-muted-foreground">
              {(error as any)?.response?.data?.error ||
                'No se pudieron cargar los reportes. Por favor, intenta nuevamente.'}
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-3">
        {/* Header */}
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center">
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              <FileText className="h-6 w-6" />
              Reportes de Registros
            </h1>
            <button
              onClick={() => setShowInfo(!showInfo)}
              className="text-muted-foreground hover:text-foreground transition-colors ml-2"
            >
              <Info className="h-5 w-5" />
            </button>
            {showInfo && (
              <span className="text-muted-foreground ml-2 text-sm animate-in slide-in-from-left-2 duration-200">
                Accede a los reportes de bases de datos externas configuradas
              </span>
            )}
          </div>
          {isTonalAdmin && (
            <Button onClick={() => navigate('/registros/configuracion')} className="bg-teal-800 hover:bg-teal-900 text-white">
              <Filter className="mr-2 h-4 w-4" />
              Configuración
            </Button>
          )}
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col gap-3 md:flex-row md:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar reportes por título o grupo..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Button
            variant={showInactive ? 'default' : 'outline'}
            size="sm"
            onClick={() => setShowInactive(!showInactive)}
          >
            {showInactive ? 'Mostrar solo activos' : 'Mostrar todos'}
          </Button>
        </div>

        {/* Stats */}
        {data && (
          <Badge className="badge-gradient text-xs inline-flex items-center gap-1.5 w-fit">
            <Database className="h-3 w-3" />
            {data.count} {data.count === 1 ? 'reporte disponible' : 'reportes disponibles'}
          </Badge>
        )}

        {/* Reporte Adium — solo visible para admin o Adium (Vertismed) */}
        {(isTonalAdmin || user?.groups?.includes('Adium (Vertismed)')) && (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 max-w-full w-full">
            <Card
              className="border border-gray-300 dark:border-gray-700 hover:shadow-lg transition-shadow cursor-pointer group"
              onClick={() => navigate('/registros/adium')}
            >
              <CardHeader>
                <div className="flex items-start justify-between">
                  <CardTitle className="text-lg group-hover:text-gray-600 transition-colors">
                    Registros Vozama
                  </CardTitle>
                  <Badge className="badge-gradient">Activo</Badge>
                </div>
                <CardDescription className="flex items-center gap-2">
                  <Database className="h-3 w-3" />
                  Adium (Vertismed)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground flex items-center gap-1">
                      <CalendarDays className="h-3 w-3" />
                      Fecha Inicio:
                    </span>
                    <span className="text-xs">14/08/2026</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground flex items-center gap-1">
                      <Link className="h-3 w-3" />
                      URL Registro:
                    </span>
                    <a
                      href="https://registro.tallervonoprazan.tonalstrategies.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline text-xs"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Ver formulario
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground pt-2 border-t">
                  <Calendar className="h-3 w-3" />
                  Creado: 14/08/2026
                </div>
                <div className="flex flex-col gap-2 mt-4">
                  <Button
                    className="w-full bg-teal-800 hover:bg-teal-900 text-white"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); navigate('/registros/adium') }}
                  >
                    <Eye className="mr-2 h-4 w-4" />
                    Ver Reporte
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Reports Grid */}
        {isLoading ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[...Array(6)].map((_, i) => (
              <Card key={i}>
                <CardHeader>
                  <Skeleton className="h-5 w-3/4 mb-2" />
                  <Skeleton className="h-4 w-1/2" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-20 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : data && data.results.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 max-w-full w-full">
            {data.results.map((config) => (
              <Card
                key={config.id}
                className="border border-gray-300 dark:border-gray-700 hover:shadow-lg transition-shadow cursor-pointer group"
                onClick={() => handleViewReport(config)}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-lg group-hover:text-gray-600 transition-colors">
                      {config.titulo_reporte}
                    </CardTitle>
                    <Badge className={config.activo ? 'badge-gradient' : 'bg-gray-100 text-gray-500 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-400'}>
                      {config.activo ? 'Activo' : 'Inactivo'}
                    </Badge>
                  </div>
                  <CardDescription className="flex items-center gap-2">
                    <Database className="h-3 w-3" />
                    {config.grupo_nombre}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {isTonalAdmin ? (
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Motor:</span>
                        <Badge variant="outline">{config.db_engine.toUpperCase()}</Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Tabla:</span>
                        <span className="font-mono text-xs">{config.tabla_nombre}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Host:</span>
                        <span className="font-mono text-xs truncate max-w-[150px]">{config.db_host}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2 text-sm">
                      {config.url_registro && (
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground flex items-center gap-1">
                            <Link className="h-3 w-3" />
                            URL Registro:
                          </span>
                          <a
                            href={config.url_registro}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline text-xs truncate max-w-[150px]"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Ver formulario
                          </a>
                        </div>
                      )}
                      {config.fecha_inicio && (
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground flex items-center gap-1">
                            <CalendarDays className="h-3 w-3" />
                            Fecha Inicio:
                          </span>
                          <span className="text-xs">{new Date(config.fecha_inicio).toLocaleDateString('es-EC')}</span>
                        </div>
                      )}
                      {config.fecha_fin && (
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground flex items-center gap-1">
                            <CalendarDays className="h-3 w-3" />
                            Fecha Fin:
                          </span>
                          <span className="text-xs">{new Date(config.fecha_fin).toLocaleDateString('es-EC')}</span>
                        </div>
                      )}
                      {!config.url_registro && !config.fecha_inicio && !config.fecha_fin && (
                        <div className="text-xs text-muted-foreground italic">Sin información adicional configurada</div>
                      )}
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-xs text-muted-foreground pt-2 border-t">
                    <Calendar className="h-3 w-3" />
                    Creado: {new Date(config.fecha_creacion).toLocaleDateString('es-EC')}
                  </div>
                  <div className="flex flex-col gap-2 mt-4">
                    <Button
                      className="w-full bg-teal-800 hover:bg-teal-900 text-white"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); handleViewReport(config) }}
                    >
                      <Eye className="mr-2 h-4 w-4" />
                      Ver Reporte
                    </Button>
                    {config.asistencia_presencial && (
                      <Button
                        variant="outline"
                        className="w-full border-blue-500 text-blue-600 hover:bg-blue-50 dark:border-blue-400 dark:text-blue-400 dark:hover:bg-blue-950"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); navigate(`/registros/asistencias/${config.id}`) }}
                      >
                        <Eye className="mr-2 h-4 w-4" />
                        Ver Asistencias
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <FileText className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No se encontraron reportes</h3>
            <p className="text-sm text-muted-foreground mb-4">
              {search ? 'No hay reportes que coincidan con tu búsqueda.' : 'No hay reportes configurados aún.'}
            </p>
            {isTonalAdmin && (
              <Button onClick={() => navigate('/registros/configuracion')} className="bg-teal-800 hover:bg-teal-900 text-white">
                <Filter className="mr-2 h-4 w-4" />
                Ir a Configuración
              </Button>
            )}
          </div>
        )}
    </div>
  )
}
